import { Button } from '@medikei/ui';
import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useMemedPrescription, extractMemedPrescriptionId } from '@/hooks/useMemedPrescription';
import { useMemedConfig } from '@/hooks/useMemedConfig';
import { FileText, FilePlus, AlertCircle, AlertTriangle, Loader2, Printer } from 'lucide-react';
import type { MemedPatientPayload, MemedWorkplace, MemedPrescriptionContext } from '@/hooks/useMemedPrescription';
import { mapAllergiesToMemedIds, type MedikeiAllergy } from '@/hooks/memed/memed-allergy.mapper';
import type { MedikeiChronicCondition } from '@/hooks/memed/memed-condition.mapper';
import type { AddItemPayload } from '@/hooks/memed/memed-item.mapper';
import { buildMemedWorkplace } from '@/hooks/memed/memed-workplace.mapper';
import { buildFeatureTogglePayload } from '@/hooks/memed/memed-feature-toggle.mapper';
import {
  buildMemedAdditionalData,
  type MemedPatientProfile,
  type MemedDoctorInfo,
} from '@/hooks/memed/memed-additional-data.mapper';

export type { MemedPatientProfile, MemedDoctorInfo } from '@/hooks/memed/memed-additional-data.mapper';

interface MemedPrescriptionButtonProps {
  patient: MemedPatientProfile;
  workplace?: {
    id: string;
    name: string;
    address: string;
    phone?: string;
    city: string;
    state: string;
  };
  allergies?: MedikeiAllergy[];
  chronicDiseases?: MedikeiChronicCondition[];
  appointmentId?: string;
  professionalId?: string;
  doctor?: MemedDoctorInfo;
  recordNumber?: string;
  items?: AddItemPayload[];
  enableReceituarioTheme?: boolean;
  memedPrescriptionId?: string;
  onPrescriptionPrinted?: (memedPrescriptionId: string) => void;
  onPrescriptionDeleted?: (memedPrescriptionId: string) => void;
  onError?: (error: string) => void;
  onSuccess?: () => void;
}

export function MemedPrescriptionButton({
  patient,
  workplace,
  allergies = [],
  chronicDiseases = [],
  appointmentId,
  professionalId,
  doctor,
  recordNumber,
  items,
  enableReceituarioTheme = true,
  memedPrescriptionId,
  onPrescriptionPrinted,
  onPrescriptionDeleted,
  onError,
  onSuccess,
}: MemedPrescriptionButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isReprinting, setIsReprinting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inFlightRef = useRef(false);
  const onPrescriptionPrintedRef = useRef(onPrescriptionPrinted);
  onPrescriptionPrintedRef.current = onPrescriptionPrinted;
  const onPrescriptionDeletedRef = useRef(onPrescriptionDeleted);
  onPrescriptionDeletedRef.current = onPrescriptionDeleted;

  const {
    openPrescription,
    newMemedPrescription,
    viewMemedPrescription,
    onMemedEvent,
    isScriptLoaded,
    isModuleReady,
    isSettingPatient,
    isSettingAllergies,
    error,
    setError,
  } = useMemedPrescription();

  const { data: memedConfig } = useMemedConfig();

  const { unmapped } = useMemo(() => mapAllergiesToMemedIds(allergies), [allergies]);

  const memedPatient: MemedPatientPayload = {
    idExterno: patient.id,
    nome: patient.name || '',
    sexo: patient.gender === 'MALE' ? 'Masculino' : patient.gender === 'FEMALE' ? 'Feminino' : '',
    cpf: patient.cpf?.replace(/\D/g, ''),
    data_nascimento: patient.birthDate
      ? new Date(patient.birthDate).toLocaleDateString('pt-BR')
      : undefined,
    telefone: patient.phone?.replace(/\D/g, ''),
    email: patient.email || undefined,
    peso: patient.healthProfile?.weight ?? undefined,
    altura: patient.healthProfile?.height ?? undefined,
  };

  const memedWorkplace: MemedWorkplace | undefined = useMemo(() => {
    const propWorkplace = workplace
      ? {
          city: workplace.city,
          state: workplace.state,
          cnes: workplace.id,
          local_name: workplace.name,
          address: workplace.address,
          phone: workplace.phone,
        }
      : {};
    return buildMemedWorkplace({ ...memedConfig?.workplace, ...propWorkplace });
  }, [workplace, memedConfig]);

  const featureToggles = useMemo(
    () => buildFeatureTogglePayload(memedConfig?.featureToggles),
    [memedConfig]
  );

  const additionalData = useMemo(
    () => buildMemedAdditionalData({ ...patient, recordNumber }, doctor),
    [patient, recordNumber, doctor]
  );

  const receituarioTheme = useMemo(() => {
    if (!enableReceituarioTheme) {
      return undefined;
    }
    const theme = memedConfig?.receituarioTheme;
    return typeof theme === 'number' && theme >= 1 && theme <= 4 ? theme : undefined;
  }, [enableReceituarioTheme, memedConfig]);

  const handleClick = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);
    setLocalError(null);
    setError(null);

    const prescriptionContext: MemedPrescriptionContext = {
      ...(appointmentId ? { appointmentId } : {}),
      ...(professionalId ? { professionalId } : {}),
    };

    try {
      const result = await openPrescription(
        memedPatient,
        memedWorkplace,
        allergies,
        chronicDiseases,
        prescriptionContext,
        { featureToggles, additionalData, items, receituarioTheme }
      );

      if (!result.success) {
        const errorMsg = result.error || 'Erro ao abrir prescrição';
        setLocalError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao abrir prescrição';
      setLocalError(errorMessage);
      onError?.(errorMessage);
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [openPrescription, memedPatient, memedWorkplace, allergies, chronicDiseases, appointmentId, professionalId, featureToggles, additionalData, items, receituarioTheme, onError, onSuccess, setError]);

  const handleNewPrescriptionClick = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setIsLoading(true);
    setLocalError(null);
    setError(null);

    try {
      const result = await newMemedPrescription();

      if (!result.success) {
        const errorMsg = result.error || 'Erro ao criar nova prescrição';
        setLocalError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      onSuccess?.();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar nova prescrição';
      setLocalError(errorMessage);
      onError?.(errorMessage);
    } finally {
      inFlightRef.current = false;
      setIsLoading(false);
    }
  }, [newMemedPrescription, onError, onSuccess, setError]);

  useEffect(() => {
    const unsubscribePrinted = onMemedEvent('prescricaoImpressa', (prescriptionData) => {
      const memedId = extractMemedPrescriptionId(prescriptionData);
      if (memedId) {
        onPrescriptionPrintedRef.current?.(memedId);
      }
    });

    const unsubscribeDeleted = onMemedEvent('prescricaoExcluida', (prescriptionId) => {
      onPrescriptionDeletedRef.current?.(String(prescriptionId));
    });

    return () => {
      unsubscribePrinted();
      unsubscribeDeleted();
    };
  }, [onMemedEvent]);

  const handleReprintClick = useCallback(async () => {
    if (!memedPrescriptionId || isReprinting) {
      return;
    }

    setIsReprinting(true);
    setLocalError(null);
    setError(null);

    try {
      const result = await viewMemedPrescription(memedPrescriptionId);

      if (!result.success) {
        const errorMsg = result.error || 'Erro ao reimprimir prescrição';
        setLocalError(errorMsg);
        onError?.(errorMsg);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao reimprimir prescrição';
      setLocalError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsReprinting(false);
    }
  }, [memedPrescriptionId, isReprinting, viewMemedPrescription, onError, setError]);

  const isDisabled = isLoading || isSettingPatient || isSettingAllergies || (!isScriptLoaded && !isModuleReady);
  const displayError = localError || error;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          onClick={handleClick}
          disabled={isDisabled}
        >
          {isLoading || isSettingPatient || isSettingAllergies ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Preparando prescrição...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Nova Prescrição
            </>
          )}
        </Button>
        {isModuleReady && (
          <Button
            variant="outline"
            onClick={handleNewPrescriptionClick}
            disabled={isLoading || isSettingPatient || isSettingAllergies}
          >
            {isLoading || isSettingPatient || isSettingAllergies ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Preparando prescrição...
              </>
            ) : (
              <>
                <FilePlus className="mr-2 h-4 w-4" />
                Nova prescrição
              </>
            )}
          </Button>
        )}
        {memedPrescriptionId && (
          <Button
            variant="outline"
            onClick={handleReprintClick}
            disabled={isReprinting || isDisabled}
          >
            {isReprinting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Reimprimindo...
              </>
            ) : (
              <>
                <Printer className="mr-2 h-4 w-4" />
                Reimprimir
              </>
            )}
          </Button>
        )}
      </div>
      {unmapped.length > 0 && (
        <div className="flex items-start gap-2 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            {unmapped.length} alergia(s) sem correspondência na MeMed ({unmapped.join(', ')}): não gerarão alerta de alergia na prescrição.
          </span>
        </div>
      )}
      {displayError && (
        <div className="flex items-start gap-2 text-sm text-red-500">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="flex-1 space-y-2">
            <span>{displayError}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClick}
              disabled={isLoading}
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
